import { useState } from 'react';
import { ApiError, normalisePhone, prettyPhone, useAction, useResource } from '@limuzin/kit';
import type { ConfigView, StartedProofView } from '@limuzin/passenger';
import {
  cachedSupportPhone, getConfig, getMe, register, restorePassword, signIn, startProof,
} from '../api';
import { useSession } from '../session';
import { legalDocumentsLive } from '../consent';
import { InstallHelp, isInstalled } from '../install';
import { Icon } from '../material';
import {
  BackDisc, Button, ErrorNote, EyeButton, LabelledField, Note, PhoneField, Soft,
} from '../widgets';
import { ProofScreen } from './proof';

/**
 * «Войдите в аккаунт» / «Создайте аккаунт» — one screen with two tabs, and
 * «Восстановление доступа» behind the third door. A port of `auth_screen.dart` and
 * `reset_screen.dart`.
 *
 * What differs between the tabs is what has to be proved. Registering proves the
 * number, with the code texted to it on «Подтвердите номер» (ADR 0052).
 * Signing in does not, because registration already did and the password stands in
 * its place (ADR 0008). The number and the password are one pair of fields shared
 * between the tabs: somebody who types a number, finds they have no account and
 * taps «Регистрация» should still be looking at their number.
 *
 * Under the number «Регистрация» asks for consent to the passenger's data being
 * processed: a box of its own, unticked, that «Подтвердить номер» waits for, since
 * proving the number is the first thing that sends anything. Under the box are the
 * text of the consent and the agreement registering accepts, each in a tab of its
 * own. The consent is a document apart from the agreement (ст. 9 152-ФЗ), so the
 * box is about it alone, and the agreement is only named.
 *
 * Under the button, on both tabs, «Позвонить в поддержку» dials the Телефон поддержки:
 * somebody who cannot get in has nowhere else here to ask. It is the number as the
 * server last gave it, on this visit or an earlier one; with none known it is not there.
 *
 * Under that, «Нужна помощь?» opens how to put the app on the phone's home screen,
 * which is how most passengers will come back to it. Already opened from there, the
 * page has nothing to teach and the link is gone.
 *
 * «Восстановление доступа» on a number with no account hands its proof over to
 * «Регистрация», with the password just chosen, so the passenger is not texted a
 * second code for the number they have proved already.
 */
export function LoginScreen() {
  const [form, setForm] = useState<'auth' | 'reset'>('auth');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [handoff, setHandoff] = useState<Proved | null>(null);

  if (form === 'reset') {
    return (
      <ResetForm
        phone={phone}
        setPhone={setPhone}
        onBack={() => setForm('auth')}
        onRegister={(proved, chosen) => {
          setPassword(chosen);
          setHandoff(proved);
          setForm('auth');
        }}
      />
    );
  }
  return (
    <AuthForm
      phone={phone}
      setPhone={setPhone}
      password={password}
      setPassword={setPassword}
      handoff={handoff}
      onReset={() => setForm('reset')}
    />
  );
}

/** A number «Регистрация» has proved, and whether it already has an account. */
interface Proved {
  phone: string;
  token: string;
  registered: boolean;
  /**
   * Proved on «Восстановление доступа», which asks no consent: «Регистрация» asks for
   * it beside the name instead of under the number.
   */
  fromRestore?: boolean;
}

const HEADS = [
  'Войдите в аккаунт',
  'Создайте аккаунт',
  'Создайте аккаунт',
  'Аккаунт уже есть',
];

const LEADS = [
  'Если у вас ещё нет аккаунта, перейдите на вкладку «Регистрация»',
  'Сначала подтвердите номер: мы пришлём код в SMS',
  'Номер подтверждён. Осталось представиться и придумать пароль',
  'Номер подтверждён, и на нём уже зарегистрирован аккаунт',
];

/**
 * «Регистрация» asks for the number alone first, and for the name and the password
 * once it is proved (ADR 0023), so a number that already has an account is found out
 * before anything else is typed.
 */
function AuthForm({
  phone,
  setPhone,
  password,
  setPassword,
  handoff,
  onReset,
}: {
  phone: string;
  setPhone: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  /** A number «Восстановление доступа» proved and found no account on. */
  handoff: Proved | null;
  onReset: () => void;
}) {
  const { api, signedIn } = useSession();
  const config = useResource<ConfigView>(() => getConfig(api), [api]);
  const support = config.data?.supportPhone ?? cachedSupportPhone();

  const [tab, setTab] = useState<'login' | 'register'>(handoff ? 'register' : 'login');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [shown, setShown] = useState(false);
  // «Автоматический вход». What is kept is the session, never the password: unticked,
  // it lasts as long as this tab.
  const [remember, setRemember] = useState(true);
  const [consent, setConsent] = useState(false);
  const [help, setHelp] = useState(false);
  // The box ticked, or no box shown.
  const consented = consent || !legalDocumentsLive;
  const [proving, setProving] = useState<{ phone: string; started: StartedProofView } | null>(
    null,
  );
  // Kept across a switch to «Вход» and back; «Изменить» drops it.
  const [proved, setProved] = useState<Proved | null>(handoff);

  const isLogin = tab === 'login';
  const step = !proved ? 'phone' : proved.registered ? 'taken' : 'details';
  const normalised = normalisePhone(phone);

  const submit = useAction(async () => {
    if (!isLogin && step === 'taken') {
      // Over to «Вход», where the number is still in its field.
      setProved(null);
      setTab('login');
      return;
    }
    if (!isLogin && step === 'details') {
      const session = await register(api, {
        verificationToken: proved!.token,
        firstName: first.trim(),
        ...(last.trim() ? { lastName: last.trim() } : {}),
        password,
        ...(legalDocumentsLive ? { consent } : {}),
      }).catch((error: unknown) => {
        // Half an hour after the code the proof is no longer good, and the message
        // says to prove the number again: back to the step that does.
        if (error instanceof ApiError && error.code === 'verification_expired') setProved(null);
        throw error;
      });
      // Registering is somebody setting the account up here, so the session is kept;
      // only «Вход» offers the choice.
      api.adopt(session);
      signedIn(await getMe(api));
      return;
    }
    if (!normalised) throw new ApiError('bad_phone', 'Введите номер телефона полностью');
    if (isLogin) {
      const session = await signIn(api, normalised, password);
      api.adopt(session, remember);
      signedIn(await getMe(api));
      return;
    }
    // «Подтвердите номер». Started here, so a refusal — no gateway online, too many
    // tries — stays under this button rather than on a screen with nothing to wait for.
    setProving({ phone: normalised, started: await startProof(api, normalised) });
  });

  const ready = isLogin
    ? Boolean(normalised) && password.length >= 6
    : step === 'phone'
      ? Boolean(normalised) && consented
      : step === 'details'
        ? first.trim().length > 0 && password.length >= 6 && consented
        : true;

  if (proving) {
    return (
      <ProofScreen
        phone={proving.phone}
        started={proving.started}
        restart={() => startProof(api, proving.phone)}
        onProved={(token, answer) => {
          setProving(null);
          setProved({ phone: proving.phone, token, registered: answer.registered === true });
        }}
        onBack={() => setProving(null)}
      />
    );
  }

  const which = isLogin ? 0 : step === 'phone' ? 1 : step === 'details' ? 2 : 3;
  const go = () => {
    if (ready && !submit.pending) void submit.run();
  };

  // Under the number, before it is proved; beside the name for a number
  // «Восстановление доступа» proved, which never asked.
  const consentBox = legalDocumentsLive && (
    <>
      <button
        type="button"
        className="remember"
        role="checkbox"
        aria-checked={consent}
        onClick={() => setConsent(!consent)}
      >
        <span className={consent ? 'checkbox on' : 'checkbox'}>
          {consent && <Icon name="check_rounded" size={13} />}
        </span>
        Даю согласие на обработку персональных данных
      </button>
      <p className="caption legal-note">
        <a href="/legal/consent-passenger.html" target="_blank" rel="noopener">
          Текст согласия
        </a>
        . Регистрируясь, вы принимаете{' '}
        <a href="/legal/offer.html" target="_blank" rel="noopener">
          Публичную оферту
        </a>
        .
      </p>
    </>
  );

  const passwordField = (
    <LabelledField
      label={isLogin ? 'Пароль' : 'Придумайте пароль'}
      type={shown ? 'text' : 'password'}
      // The browser's own password manager keeps the secret somewhere this page
      // cannot, which is the only place a password belongs.
      autoComplete={isLogin ? 'current-password' : 'new-password'}
      placeholder="••••••••"
      value={password}
      onChange={(event) => setPassword(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') go();
      }}
      trailing={<EyeButton shown={shown} onClick={() => setShown(!shown)} />}
    />
  );

  return (
    <div className="foot-page">
      <div className="column auth">
        <div>
          <div className="auth-brand">
            <div className="label">Лимузин</div>
            {/* Every tab's words laid out and only one shown, so the box is always
                as tall as the tallest and the tabs under it never move. */}
            <div className="whichever">
              {HEADS.map((head, i) => (
                <h1 key={i} aria-hidden={i !== which}>
                  {head}
                </h1>
              ))}
            </div>
            <div className="whichever">
              {LEADS.map((lead, i) => (
                <p key={i} aria-hidden={i !== which}>
                  {lead}
                </p>
              ))}
            </div>
          </div>

          <div className="auth-tabs" role="tablist">
            {(['login', 'register'] as const).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={key === tab}
                className={key === tab ? 'on' : ''}
                disabled={submit.pending}
                onClick={() => {
                  setTab(key);
                  submit.clearError();
                }}
              >
                {key === 'login' ? 'Вход' : 'Регистрация'}
              </button>
            ))}
          </div>

          <div className="stack" style={{ marginTop: 16, gap: 10 }}>
            {!isLogin && proved ? (
              <div className="proved-phone">
                <div className="grow">
                  <span className="row" style={{ gap: 6, alignItems: 'baseline' }}>
                    <span className="label">Телефон</span>
                    <span className="strong" style={{ fontSize: 12, color: 'var(--go-text)' }}>
                      подтверждён
                    </span>
                  </span>
                  <div className="strong" style={{ fontSize: 16, padding: '7px 0 2px' }}>
                    {prettyPhone(proved.phone)}
                  </div>
                </div>
                <Soft style={{ padding: '9px 14px' }} disabled={submit.pending} onClick={() => setProved(null)}>
                  Изменить
                </Soft>
              </div>
            ) : (
              <PhoneField value={phone} onChange={setPhone} onEnter={go} />
            )}

            {isLogin && (
              <>
                {passwordField}
                <button
                  type="button"
                  className="remember"
                  role="checkbox"
                  aria-checked={remember}
                  onClick={() => setRemember(!remember)}
                >
                  <span className={remember ? 'checkbox on' : 'checkbox'}>
                    {remember && <Icon name="check_rounded" size={13} />}
                  </span>
                  Автоматический вход
                </button>
              </>
            )}

            {!isLogin && step === 'phone' && consentBox}

            {!isLogin && step === 'taken' && (
              <Note>
                На этом номере уже есть аккаунт. Войдите с паролем, а если не помните его —
                нажмите «Забыли пароль?» на вкладке «Вход».
              </Note>
            )}

            {!isLogin && step === 'details' && (
              <>
                <div>
                  {passwordField}
                  <p className="caption" style={{ margin: '8px 4px 0', fontSize: 12.5 }}>
                    Не менее 6 символов.
                  </p>
                </div>
                <LabelledField
                  label="Имя"
                  autoComplete="given-name"
                  placeholder="Анна"
                  value={first}
                  onChange={(event) => setFirst(event.target.value)}
                />
                <LabelledField
                  label="Фамилия"
                  optional
                  autoComplete="family-name"
                  placeholder="Королёва"
                  value={last}
                  onChange={(event) => setLast(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') go();
                  }}
                />
                {proved?.fromRestore && consentBox}
              </>
            )}
          </div>

          <ErrorNote error={submit.error} />

          {isLogin && (
            <p style={{ textAlign: 'center', margin: '14px 0 0' }}>
              <button type="button" className="linkish" disabled={submit.pending} onClick={onReset}>
                Забыли пароль?
              </button>
            </p>
          )}
        </div>

        {/* The button sits at the foot of a short form and is pushed down by a long
            one. */}
        <div className="foot-spacer" />
        <Button busy={submit.pending} disabled={!ready} onClick={() => void submit.run()}>
          {isLogin || step === 'taken'
            ? 'Войти'
            : step === 'phone'
              ? 'Подтвердить номер'
              : 'Зарегистрироваться'}
        </Button>
        {support && (
          <p style={{ textAlign: 'center', margin: '12px 0 0' }}>
            <a className="linkish call-support" href={`tel:${support}`}>
              <Icon name="call_outlined" size={17} />
              Позвонить в поддержку
            </a>
          </p>
        )}
        {!isInstalled() && (
          <p style={{ textAlign: 'center', margin: support ? 0 : '12px 0 0' }}>
            <button type="button" className="linkish quiet" onClick={() => setHelp(true)}>
              Нужна помощь?
            </button>
          </p>
        )}
      </div>
      {help && <InstallHelp onClose={() => setHelp(false)} />}
    </div>
  );
}

/**
 * «Восстановление доступа» — the number, a new password, and the same «Подтвердите
 * номер» that registering ends in (ADR 0022). Nothing arrives with a password in it:
 * the passenger chooses one here, proves the number with the code texted to it (ADR
 * 0052), and is signed in on the spot; every other place the account was is signed out.
 */
function ResetForm({
  phone,
  setPhone,
  onBack,
  onRegister,
}: {
  phone: string;
  setPhone: (value: string) => void;
  onBack: () => void;
  /** «Зарегистрироваться» on a number proved here and found with no account. */
  onRegister: (proved: Proved, password: string) => void;
}) {
  const { api, signedIn } = useSession();
  const [password, setPassword] = useState('');
  const [shown, setShown] = useState(false);
  const [proving, setProving] = useState<{ phone: string; started: StartedProofView } | null>(
    null,
  );

  // The number proved and found with no account; gone once another is typed.
  const [noAccount, setNoAccount] = useState<{ phone: string; token: string } | null>(null);

  const normalised = normalisePhone(phone);
  const ready = Boolean(normalised) && password.length >= 6;
  const unregistered = noAccount && noAccount.phone === normalised ? noAccount : null;

  const start = useAction(async () => {
    if (!normalised) throw new ApiError('bad_phone', 'Введите номер телефона полностью');
    setProving({ phone: normalised, started: await startProof(api, normalised, 'RESTORE') });
  });

  const finish = useAction(async (proved: string, verificationToken: string) => {
    const session = await restorePassword(api, { verificationToken, password }).catch(
      (error: unknown) => {
        // Said under the button, with «Зарегистрироваться» in its place: the proof
        // is still good, and registers the number without another SMS.
        if (error instanceof ApiError && error.code === 'no_account') {
          setNoAccount({ phone: proved, token: verificationToken });
          return null;
        }
        throw error;
      },
    );
    if (!session) return;
    // Somebody setting the account up on this device again, as registering is.
    api.adopt(session);
    signedIn(await getMe(api));
  });

  if (proving) {
    return (
      <ProofScreen
        phone={proving.phone}
        started={proving.started}
        restart={() => startProof(api, proving.phone, 'RESTORE')}
        onProved={async (token) => {
          // Back on the form, whatever happens next: a number with no account is said
          // under its button.
          setProving(null);
          await finish.run(proving.phone, token);
        }}
        onBack={() => setProving(null)}
      />
    );
  }

  return (
    <div className="foot-page">
      <div className="column reset">
        <BackDisc onClick={onBack} />
        <h1 className="title" style={{ fontSize: 28, marginTop: 18 }}>
          Восстановление доступа
        </h1>
        <p className="body" style={{ margin: '10px 0 0', maxWidth: 300, fontSize: 16, color: 'var(--body)' }}>
          Придумайте новый пароль и подтвердите номер кодом из SMS. Остальные устройства
          выйдут из аккаунта.
        </p>

        <div className="stack" style={{ marginTop: 22, gap: 10 }}>
          <PhoneField value={phone} onChange={setPhone} autoFocus />
          <LabelledField
            label="Новый пароль"
            type={shown ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="••••••••"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && ready) void start.run();
            }}
            trailing={<EyeButton shown={shown} onClick={() => setShown(!shown)} />}
          />
        </div>
        <p className="caption" style={{ margin: '8px 4px 0', fontSize: 12.5 }}>
          Не менее 6 символов.
        </p>

        {unregistered ? (
          <Note style={{ marginTop: 14 }}>
            На номере {prettyPhone(unregistered.phone)} нет аккаунта. Зарегистрируйтесь.
          </Note>
        ) : (
          <ErrorNote error={start.error ?? finish.error} />
        )}

        <div style={{ marginTop: 14 }}>
          <Button
            busy={start.pending || finish.pending}
            disabled={!ready}
            style={{ padding: 17 }}
            onClick={() =>
              unregistered
                ? onRegister(
                  { ...unregistered, registered: false, fromRestore: true },
                  password,
                )
                : void start.run()
            }
          >
            {unregistered ? 'Зарегистрироваться' : 'Подтвердить номер'}
          </Button>
        </div>
      </div>
    </div>
  );
}
